import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Info, Landmark, Users, Clock, Activity, ChevronUp, ChevronDown, ArrowRightLeft } from "lucide-react";
import { feature } from "topojson-client";
import { geoMercator, geoPath } from "d3-geo";
import { scaleQuantile } from "d3-scale";
import topo from "../../geo/es-comunidades.topo.json";
import { CCAA, INDICADORES } from "../../data/mapaCCAA.js";
import Logo from "../../components/site/Logo";
import Footer from "../../components/site/Footer";
import useSeo from "../../hooks/useSeo";

const fc = feature(topo, topo.objects.autonomous_regions);
const mainFC = {
  type: "FeatureCollection",
  features: fc.features.filter((f) => CCAA[f.id] && f.id !== "05"),
};
const canaria = fc.features.find((f) => f.id === "05");

const W = 760;
const H = 560;

function Mapa({ indKey, selected, onSelect }) {
  const ind = INDICADORES[indKey];

  const { pathMain, pathCan, color } = useMemo(() => {
    const projMain = geoMercator().fitExtent([[16, 8], [744, 452]], mainFC);
    const projCan = geoMercator().fitExtent(
      [[16, 470], [196, 548]],
      { type: "FeatureCollection", features: [canaria] }
    );
    const values = Object.values(CCAA).map((c) => c[ind.key]);
    return {
      pathMain: geoPath(projMain),
      pathCan: geoPath(projCan),
      color: scaleQuantile().domain(values).range(ind.ramp),
    };
  }, [ind]);

  const renderFeat = (f, pathGen) => {
    const d = CCAA[f.id];
    if (!d) return null;
    const isSel = selected === f.id;
    return (
      <path
        key={f.id}
        d={pathGen(f)}
        data-testid={`mapa-ccaa-${f.id}`}
        onClick={() => onSelect(f.id)}
        className="cursor-pointer transition-[stroke-width,filter] duration-150 outline-none"
        fill={color(d[ind.key])}
        stroke={isSel ? "#111827" : "#ffffff"}
        strokeWidth={isSel ? 2 : 0.8}
        style={{ filter: isSel ? "drop-shadow(0 2px 5px rgba(0,0,0,.35))" : "none" }}
      >
        <title>{`${d.name} · ${ind.fmt(d[ind.key])}`}</title>
      </path>
    );
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Mapa de España por comunidades autónomas">
      <g>{mainFC.features.map((f) => renderFeat(f, pathMain))}</g>
      {/* recuadro Canarias */}
      <rect x="10" y="464" width="196" height="90" fill="none" stroke="#d1d5db" strokeWidth="1" strokeDasharray="4 3" rx="4" />
      <text x="14" y="476" fontSize="9" fill="#9ca3af" fontWeight="700">CANARIAS</text>
      <g>{renderFeat(canaria, pathCan)}</g>
    </svg>
  );
}

const FieldRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
    <span className="flex items-center gap-2 text-sm text-gray-500">
      <Icon size={15} className="text-gray-400" /> {label}
    </span>
    <span className="text-sm font-bold text-gray-900" style={{ color: accent }}>{value}</span>
  </div>
);

const COLS = [
  { key: "name", label: "Comunidad", align: "left", fmt: (v) => v },
  { key: "deudaTotal", label: "Deuda total", fmt: (v) => `${v.toLocaleString("es-ES")} M€` },
  { key: "deudaHab", label: "Deuda/hab.", fmt: (v) => `${v.toLocaleString("es-ES")} €` },
  { key: "pacientes", label: "Pacientes", fmt: (v) => v.toLocaleString("es-ES") },
  { key: "por1000", label: "Por 1.000 hab.", fmt: (v) => v.toLocaleString("es-ES") },
  { key: "esperaMedia", label: "Espera media", fmt: (v) => `${v} días` },
];

function RankingTable({ selected, onSelect }) {
  const [sortKey, setSortKey] = useState("deudaTotal");
  const [dir, setDir] = useState("desc");

  const rows = useMemo(() => {
    const list = Object.entries(CCAA).map(([id, d]) => ({ id, ...d }));
    list.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "string" ? va.localeCompare(vb, "es") : va - vb;
      return dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [sortKey, dir]);

  const toggle = (key) => {
    if (key === sortKey) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDir(key === "name" ? "asc" : "desc");
    }
  };

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-sm bg-white" data-testid="ranking-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {COLS.map((c) => {
              const active = c.key === sortKey;
              return (
                <th
                  key={c.key}
                  data-testid={`ranking-th-${c.key}`}
                  onClick={() => toggle(c.key)}
                  className={`px-4 py-3 font-bold text-gray-600 cursor-pointer select-none whitespace-nowrap ${
                    c.align === "left" ? "text-left" : "text-right"
                  } hover:text-espana-red`}
                >
                  <span className={`inline-flex items-center gap-1 ${c.align === "left" ? "" : "flex-row-reverse"}`}>
                    {c.label}
                    {active ? (
                      dir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    ) : (
                      <ChevronDown size={14} className="opacity-20" />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id}
              data-testid={`ranking-row-${r.id}`}
              onClick={() => onSelect(r.id)}
              className={`border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${
                selected === r.id ? "bg-espana-red/5" : "hover:bg-gray-50"
              }`}
            >
              {COLS.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-3 whitespace-nowrap ${
                    c.align === "left"
                      ? "text-left font-bold text-gray-900"
                      : "text-right tabular-nums text-gray-700"
                  } ${c.key === sortKey && c.align !== "left" ? "font-bold text-gray-900" : ""}`}
                >
                  {c.align === "left" && (
                    <span className="text-gray-400 mr-2 tabular-nums">{i + 1}.</span>
                  )}
                  {c.fmt(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CMP_ROWS = [
  { key: "deudaTotal", label: "Deuda total", accent: "#AA151B", fmt: (v) => `${v.toLocaleString("es-ES")} M€`, better: "low" },
  { key: "deudaHab", label: "Deuda / habitante", accent: "#1D4ED8", fmt: (v) => `≈ ${v.toLocaleString("es-ES")} €`, better: "low" },
  { key: "pacientes", label: "Pacientes en espera", accent: "#15803D", fmt: (v) => v.toLocaleString("es-ES"), better: "low" },
  { key: "por1000", label: "Pacientes / 1.000 hab.", accent: "#15803D", fmt: (v) => v.toLocaleString("es-ES"), better: "low" },
  { key: "esperaMedia", label: "Espera media", accent: "#B45309", fmt: (v) => `${v} días`, better: "low" },
];

function CCAASelect({ value, onChange, exclude, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full appearance-none bg-white border border-gray-300 rounded-sm px-4 py-3 font-bold text-gray-900 focus:outline-none focus:border-espana-red cursor-pointer"
    >
      {Object.entries(CCAA).map(([id, d]) => (
        <option key={id} value={id} disabled={id === exclude}>
          {d.name}
        </option>
      ))}
    </select>
  );
}

function Comparador() {
  const [a, setA] = useState("09");
  const [b, setB] = useState("13");
  const da = CCAA[a];
  const db = CCAA[b];

  return (
    <div data-testid="comparador">
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-gray-400">Comunidad A</label>
          <div className="mt-1.5"><CCAASelect value={a} onChange={setA} exclude={b} testid="comparador-select-a" /></div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-gray-400">Comunidad B</label>
          <div className="mt-1.5"><CCAASelect value={b} onChange={setB} exclude={a} testid="comparador-select-b" /></div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-sm overflow-hidden bg-white">
        <div className="grid grid-cols-2 bg-gray-900 text-white">
          <div className="px-4 py-3 font-black text-center border-r border-white/15" data-testid="comparador-nombre-a">{da.name}</div>
          <div className="px-4 py-3 font-black text-center" data-testid="comparador-nombre-b">{db.name}</div>
        </div>
        {CMP_ROWS.map((row) => {
          const va = da[row.key];
          const vb = db[row.key];
          // "mejor" = menor valor (menos deuda / menos espera). Empate: ninguno resaltado.
          const aBetter = va < vb;
          const bBetter = vb < va;
          return (
            <div key={row.key} className="border-b border-gray-100 last:border-0">
              <div className="text-center text-xs font-bold uppercase tracking-wide text-gray-400 pt-3">{row.label}</div>
              <div className="grid grid-cols-2">
                <div className={`px-4 py-2.5 text-center font-bold text-lg tabular-nums ${aBetter ? "text-green-700" : "text-gray-900"}`}>
                  {row.fmt(va)}
                  {aBetter && <span className="ml-1.5 text-xs font-bold text-green-600">▼ menor</span>}
                </div>
                <div className={`px-4 py-2.5 text-center font-bold text-lg tabular-nums ${bBetter ? "text-green-700" : "text-gray-900"}`}>
                  {row.fmt(vb)}
                  {bBetter && <span className="ml-1.5 text-xs font-bold text-green-600">▼ menor</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-gray-400">
        En verde, el valor más bajo de cada indicador (menor deuda o menor espera). E² no emite un juicio sobre la gestión: solo muestra los datos.
      </p>
    </div>
  );
}

export default function MapaEspana() {
  useSeo({
    title: "Mapa de España: deuda autonómica y listas de espera | E²",
    description:
      "Mapa interactivo de las comunidades autónomas: deuda total, deuda por habitante y pacientes en lista de espera quirúrgica. Datos oficiales del Banco de España y el Ministerio de Sanidad.",
    path: "mapa",
  });

  const [indKey, setIndKey] = useState("deudaTotal");
  const [selected, setSelected] = useState("09"); // Cataluña por defecto
  const ind = INDICADORES[indKey];
  const sel = selected ? CCAA[selected] : null;

  return (
    <div className="bg-[#FAFAFA] min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="mapa-home-link"><Logo /></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-espana-red hover:text-espana-reddark transition-colors">
            <ArrowLeft size={16} /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Intro */}
        <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-6">
          <div className="h-1.5 w-24 bg-espana-red mb-6" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-3">España por comunidades</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            Deuda y listas de espera
          </h1>
          <p className="mt-5 text-lg text-gray-600 max-w-3xl leading-relaxed">
            ¿Cuánto debe cada comunidad y cuántos ciudadanos esperan una intervención? E² presenta tres
            indicadores independientes con datos oficiales, sin crear un índice político ni atribuir causas
            a las diferencias entre comunidades.
          </p>
        </section>

        {/* Mapa + panel */}
        <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-10">
          {/* Selector de indicador */}
          <div className="flex flex-wrap gap-2.5 mb-6" data-testid="mapa-indicadores">
            {Object.values(INDICADORES).map((i) => {
              const active = i.key === indKey;
              return (
                <button
                  key={i.key}
                  data-testid={`mapa-ind-${i.key}`}
                  onClick={() => setIndKey(i.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-bold border transition-colors ${
                    active ? "text-white border-transparent" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                  }`}
                  style={active ? { backgroundColor: i.dot } : {}}
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: active ? "#fff" : i.dot }} />
                  {i.label}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-gray-500 mb-6 max-w-3xl flex items-start gap-2">
            <Info size={16} className="text-gray-400 mt-0.5 shrink-0" /> {ind.desc}
          </p>

          <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
            {/* Mapa */}
            <div className="bg-white border border-gray-200 rounded-sm p-4">
              <Mapa indKey={indKey} selected={selected} onSelect={setSelected} />
              {/* leyenda */}
              <div className="mt-2 flex items-center gap-3 px-2">
                <span className="text-xs text-gray-500">Menor</span>
                <div className="flex-1 flex h-2.5 rounded-full overflow-hidden">
                  {ind.ramp.map((c) => (
                    <span key={c} className="flex-1" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <span className="text-xs text-gray-500">Mayor</span>
              </div>
              <p className="mt-3 text-xs text-gray-400 px-2">
                El color representa la magnitud de <strong>{ind.label.toLowerCase()}</strong> respecto al resto de comunidades. Pulsa una comunidad para ver todos sus datos.
              </p>
            </div>

            {/* Panel detalle */}
            <div className="bg-white border border-gray-200 rounded-sm p-6 lg:sticky lg:top-24" data-testid="mapa-panel">
              {sel ? (
                <motion.div key={selected} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                  <p className="text-xs font-bold uppercase tracking-wide text-espana-red mb-1">Comunidad seleccionada</p>
                  <h2 className="text-2xl font-black tracking-tight leading-tight" data-testid="mapa-panel-nombre">{sel.name}</h2>
                  <div className="mt-4">
                    <FieldRow icon={Landmark} label="Deuda total" value={`${sel.deudaTotal.toLocaleString("es-ES")} M€`} accent="#AA151B" />
                    <FieldRow icon={Landmark} label="Deuda / habitante" value={`≈ ${sel.deudaHab.toLocaleString("es-ES")} €`} accent="#1D4ED8" />
                    <FieldRow icon={Users} label="Pacientes en espera" value={sel.pacientes.toLocaleString("es-ES")} accent="#15803D" />
                    <FieldRow icon={Activity} label="Pacientes / 1.000 hab." value={sel.por1000.toLocaleString("es-ES")} accent="#15803D" />
                    <FieldRow icon={Clock} label="Espera media" value={`${sel.esperaMedia} días`} accent="#B45309" />
                  </div>
                  {sel.mas6meses != null && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
                      <p className="text-xs text-amber-800">
                        <strong>{sel.mas6meses.toLocaleString("es-ES")}</strong> pacientes esperan más de seis meses.
                      </p>
                    </div>
                  )}
                </motion.div>
              ) : (
                <div className="text-center py-10 text-gray-400">
                  <MapPin size={28} className="mx-auto mb-3" />
                  <p className="text-sm">Selecciona una comunidad en el mapa para ver sus datos.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Ranking ordenable */}
        <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-4">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Ranking por comunidad</h2>
          <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
            Pulsa en cualquier columna para ordenar por deuda o por listas de espera. Haz clic en una fila para verla resaltada en el mapa.
          </p>
          <div className="mt-6">
            <RankingTable selected={selected} onSelect={setSelected} />
          </div>
        </section>

        {/* Comparador */}
        <section className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
          <div className="flex items-center gap-2.5 mb-2">
            <ArrowRightLeft size={22} className="text-espana-red" />
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Comparador de comunidades</h2>
          </div>
          <p className="mt-2 text-gray-600 max-w-3xl leading-relaxed">
            Elige dos comunidades y compara sus cifras enfrentadas, dato a dato.
          </p>
          <div className="mt-6 max-w-3xl">
            <Comparador />
          </div>
        </section>

        {/* Cifras clave */}
        <section className="bg-white border-y border-gray-200">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12 grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-black text-espana-red">346.910 M€</div>
              <p className="mt-2 text-sm text-gray-500">Deuda total de las CC. AA. (1T 2026)</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black text-espana-red">+850.000</div>
              <p className="mt-2 text-sm text-gray-500">Pacientes en lista de espera quirúrgica</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black text-espana-red">17</div>
              <p className="mt-2 text-sm text-gray-500">Comunidades autónomas comparadas</p>
            </div>
          </div>
        </section>

        {/* Tres indicadores */}
        <section className="max-w-6xl mx-auto px-5 lg:px-8 py-14">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Tres indicadores, una misma realidad</h2>
          <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
            Una comunidad puede tener una deuda total elevada por su tamaño, pero una deuda por habitante menor;
            o muchos pacientes en espera por su población, con una tasa relativamente baja. Por eso E² no convierte
            problemas distintos en una única cifra.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { c: "#AA151B", t: "Deuda", d: "¿Cuánto debe la Administración autonómica?" },
              { c: "#1D4ED8", t: "Deuda por habitante", d: "¿Cuánta deuda corresponde estadísticamente a cada habitante?" },
              { c: "#15803D", t: "Lista de espera", d: "¿Cuántas personas esperan una intervención quirúrgica?" },
              { c: "#B45309", t: "Tiempo de espera", d: "¿Cuánto tiempo espera, de media, un paciente?" },
            ].map((x) => (
              <div key={x.t} className="bg-white border border-gray-200 p-5 rounded-sm">
                <span className="h-3 w-3 rounded-full inline-block mb-3" style={{ backgroundColor: x.c }} />
                <h3 className="font-bold">{x.t}</h3>
                <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Fuentes / transparencia */}
        <section className="bg-gray-900 text-white">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14">
            <h2 className="text-2xl font-black tracking-tight">Transparencia de los datos</h2>
            <p className="mt-3 text-white/70 max-w-3xl leading-relaxed">
              E² utiliza fuentes oficiales e identifica el organismo responsable de cada indicador. Los dos indicadores
              principales no comparten exactamente el mismo período de referencia, y así se indica expresamente.
            </p>
            <div className="mt-8 grid md:grid-cols-3 gap-5">
              {[
                { o: "Banco de España", d: "Deuda de las CC. AA. según el Protocolo de Déficit Excesivo (PDE). Último dato: 1T 2026." },
                { o: "Ministerio de Sanidad", d: "Sistema de Información de Listas de Espera del SNS (SISLE-SNS). Último dato: 31/12/2025." },
                { o: "INE", d: "Datos oficiales de población utilizados para los cálculos por habitante." },
              ].map((s) => (
                <div key={s.o} className="bg-white/5 border border-white/10 p-5 rounded-sm">
                  <h3 className="font-bold text-espana-gold">{s.o}</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-white/50 max-w-3xl">
              Sin propaganda. Sin estimaciones ocultas. Sin mezclar indicadores. Datos oficiales, comparables y actualizables.
              La transparencia estadística empieza por indicar qué se mide, cuándo se mide y quién publica el dato.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
